import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Shield, Star, Heart } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-brand-charcoal text-slate-300 border-t-4 border-brand-primary">
      {/* Upper Footer Section */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Company Info */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <img
                src="https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/logo55.png"
                alt="KBS Tømrer-Montage ApS"
                className="h-12 w-auto object-contain bg-white/95 p-1.5 rounded-md shadow-sm"
              />
            </div>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              Professionelt tømrerarbejde i Malling og omegn med mere end 30 års erfaring. Vi leverer solidt håndværk, kvalitet og ærlighed i alle projekter.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex text-brand-gold">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <span className="text-xs font-semibold text-white">Medlem af 3byg</span>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="font-display text-sm font-bold tracking-wider text-white uppercase mb-5">
              Vores Ydelser
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/ydelser/toemrer" className="hover:text-brand-primary-light transition-colors">
                  Tømrerarbejde
                </Link>
              </li>
              <li>
                <Link to="/ydelser/tag" className="hover:text-brand-primary-light transition-colors">
                  Nyt Tag & Renovering
                </Link>
              </li>
              <li>
                <Link to="/ydelser/doere-vinduer" className="hover:text-brand-primary-light transition-colors">
                  Døre & Vinduer
                </Link>
              </li>
              <li>
                <Link to="/ydelser/tilbygning" className="hover:text-brand-primary-light transition-colors">
                  Tilbygninger
                </Link>
              </li>
              <li>
                <Link to="/ydelser/gulve" className="hover:text-brand-primary-light transition-colors">
                  Gulvarbejde
                </Link>
              </li>
              <li>
                <Link to="/ydelser/montage" className="hover:text-brand-primary-light transition-colors">
                  Montageopgaver
                </Link>
              </li>
              <li>
                <Link to="/ydelser/hovedentrepriser" className="hover:text-brand-primary-light transition-colors">
                  Hovedentrepriser
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Details */}
          <div>
            <h3 className="font-display text-sm font-bold tracking-wider text-white uppercase mb-5">
              Kontakt os
            </h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-brand-primary-light shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">KBS Tømrer – Montage ApS</p>
                  <p className="text-slate-400">Balskærvej 26</p>
                  <p className="text-slate-400">8340 Malling</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-brand-primary-light shrink-0" />
                <a href="tel:51219030" className="hover:text-brand-primary-light transition-colors font-medium text-white">
                  51 21 90 30
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-brand-primary-light shrink-0" />
                <a href="mailto:kbsoerensen@outlook.dk" className="hover:text-brand-primary-light transition-colors font-medium text-white break-all">
                  kbsoerensen@outlook.dk
                </a>
              </li>
            </ul>
          </div>

          {/* Areas & Sponsorships */}
          <div className="flex flex-col gap-6">
            <div>
              <h3 className="font-display text-sm font-bold tracking-wider text-white uppercase mb-4">
                Vi dækker lokalt
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Vi udfører tømrerarbejde i <span className="text-white font-medium">Malling, Odder, Skanderborg, Ry, Århus</span> og resten af Østjylland.
              </p>
            </div>
            <div>
              <h3 className="font-display text-sm font-bold tracking-wider text-white uppercase mb-4">
                Vi støtter en god sag
              </h3>
              <div className="flex items-center gap-4 flex-wrap">
                <img
                  src="https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/kc-erhvervsdonor-st-e2-94-9c-c2-a9ttelogo.jpg"
                  alt="Knæk Cancer Erhvervsdonor"
                  className="h-[90px] w-auto object-contain rounded bg-white p-1"
                />
                <span className="text-xs font-bold bg-white text-brand-charcoal px-3 py-2 rounded shadow-sm whitespace-nowrap">
                  Hospitalsklovne
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lower Footer Section */}
      <div className="bg-slate-950 py-6 text-xs text-slate-500">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {currentYear} KBS Tømrer – Montage ApS. Alle rettigheder forbeholdes. CVR: 35246714</p>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1">
              <Shield className="h-3.5 w-3.5 text-brand-primary-light" />
              Garanti for godt håndværk
            </span>
            <span className="flex items-center gap-1">
              <Heart className="h-3.5 w-3.5 text-red-500 fill-current" />
              Stolt lokal tømrermester
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
